import { Input } from '@/shared/components/ui/input'
import { cn } from '@shared/lib/utils'
import { FieldLabel } from './field-label'
import { INPUT_CLASSES } from './form-styles'

const KEYDOWN_NAVIGATION_KEYS = [
  'Backspace',
  'Delete',
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown',
  'Tab',
  'Home',
  'End',
] as const

function sanitizeAllowedInput(value: string, allowedKeyPattern: RegExp) {
  return [...value].filter((char) => allowedKeyPattern.test(char)).join('')
}

interface TextInputFieldProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'onChange'
> {
  label: string
  required?: boolean
  uppercase?: boolean
  disableCopyPaste?: boolean
  labelClassName?: string
  /** Blocks disallowed characters on keydown and strips them from pasted/autocomplete input. */
  allowedKeyPattern?: RegExp
  onChange: (value: string) => void
  inputRef?: React.RefObject<HTMLInputElement>
}

export function TextInputField({
  label,
  required,
  disableCopyPaste,
  labelClassName,
  min,
  max,
  onChange,
  className,
  type,
  uppercase = true,
  allowedKeyPattern,
  inputRef,
  onKeyDown,
  ...props
}: TextInputFieldProps) {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let newValue = event.target.value

    if (allowedKeyPattern) {
      newValue = sanitizeAllowedInput(newValue, allowedKeyPattern)
    }

    const isDateField = type === 'date'
    const isPasswordField = type === 'password'

    if (isDateField || isPasswordField || !uppercase) {
      onChange(newValue)
    } else {
      onChange(newValue.toUpperCase())
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (allowedKeyPattern) {
      const isNavigationKey = KEYDOWN_NAVIGATION_KEYS.includes(
        event.key as (typeof KEYDOWN_NAVIGATION_KEYS)[number]
      )

      if (
        !isNavigationKey &&
        !event.ctrlKey &&
        !event.metaKey &&
        event.key.length === 1 &&
        !allowedKeyPattern.test(event.key)
      ) {
        event.preventDefault()
        return
      }
    }

    if (type === 'date' && label.toLowerCase().includes('date of birth')) {
      const input = event.currentTarget
      const currentValue = input.value
      const selectionStart = input.selectionStart ?? 0

      const yearPart = currentValue.substring(0, 4)
      const hasCompleteYear = /^\d{4}$/.test(yearPart)
      const firstCharNotZero = yearPart.length > 0 && yearPart[0] !== '0'

      if (hasCompleteYear && firstCharNotZero) {
        const allowedKeys = [
          'Backspace',
          'Delete',
          'ArrowLeft',
          'ArrowRight',
          'ArrowUp',
          'ArrowDown',
          'Tab',
          'Home',
          'End',
          '-',
        ]

        if (!allowedKeys.includes(event.key) && !event.ctrlKey && !event.metaKey) {
          if (selectionStart <= 3 && /^\d$/.test(event.key)) {
            event.preventDefault()
            return
          }
        }
      }
    }

    onKeyDown?.(event)
  }

  const isDateOfBirth = type === 'date' && label.toLowerCase().includes('date of birth')
  const dateMin = isDateOfBirth ? (min ?? '1900-01-01') : min
  const dateMax = isDateOfBirth ? (max ?? new Date().toISOString().split('T')[0]) : max

  const handlePaste = disableCopyPaste ? (e: React.ClipboardEvent) => e.preventDefault() : undefined
  const handleCopy = disableCopyPaste ? (e: React.ClipboardEvent) => e.preventDefault() : undefined

  return (
    <div className="flex flex-col gap-2">
      <FieldLabel required={required} className={labelClassName}>
        {label}
      </FieldLabel>
      <Input
        {...props}
        type={type}
        ref={inputRef}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onCopy={handleCopy}
        className={cn(INPUT_CLASSES, className)}
        min={dateMin}
        max={dateMax}
      />
    </div>
  )
}
